import { ExportFormat } from '@/core/export'
import { DiagnosticId, type DiagnosticRow } from '@/app/diagnostics/types'
import { canShareFiles, createShareCapabilityFiles } from '@/core/export/share'
import { listRecentEdits } from '@/core/persistence'
import { getReportingStatus, setReportingTag } from '@/core/reporting/runtime'
import { ReportingTag } from '@/core/reporting/types'
import { cachedMaxCanvasArea, maxSquareCanvasSide, probeMaxCanvasArea } from '@/core/render/canvasLimits'
import { SHARE_TARGET_METHOD, SHARE_TARGET_PATH } from '@/core/shareTarget'

const WORKER_PROBE_TIMEOUT_MS = 5_000
const HEIC_FIXTURE_BYTES = 250
const AVIF_FIXTURE_BYTES = 105
// Minimal decoder corpus images from strukturag/libheif, embedded so diagnostics never uploads or fetches a photo.
const HEIC_FIXTURE_BASE64 = 'AAAAEGZ0eXBtaWYzaGVpYwAAAOptaW5pCAg/fiBuANYBA3AAAAAAAAAAAAAe8AD8/fj4AAAPA2AAAQAYQAEMAf//A3AAAAMAkAAAAwAAAwAeugJAYQABACpCAQEDcAAAAwCQAAADAAADAB6gIIEFlurkprm4CGgwIAAAAwMgAAADACFiAAEABkQBwXPAiQAAAGgoAa8TgPUrAhGDczL1mz4HCRRzxqbGjnnUrr1cLTO799zRz6nw0QjRMp+4I2Da10D3ghQEMvB53CWoI0S3qXIb99YsvLFaQ9ZLHxsJsZ9SxlvNJ5EgD4Y4miuaKu3bxPGXDHirp/9TzA=='
const AVIF_FIXTURE_BASE64 = 'AAAAEGZ0eXBtaWYzYXZpZgAAAFltaW5pCAgfPggBFIEADAASAAoJGBE/9ggIaDQgMjcYAAooooQAMzrj42WYb0ErshxaPUx2yx4WHas9hb/0eeB5BWes7UTyP61FRBjTe4YThF7ooxqA'
const SHARE_TARGET_MISSING_FILES_MESSAGE = 'Share Target requires at least one'

interface NavigatorWithDeviceMemory extends Navigator {
  readonly deviceMemory?: number
}

interface ProbeResult {
  value: string
  ok: boolean
}

interface ManifestShape {
  share_target?: {
    action?: unknown
  }
}

interface DiagnosticDefinition {
  id: DiagnosticId
  name: string
  tag: ReportingTag
  probe: () => ProbeResult | Promise<ProbeResult>
}

function result(value: string, ok = true): ProbeResult {
  if (!value.trim()) throw new Error('Diagnostic probe returned an empty value')
  return { value, ok }
}

function errorText(error: unknown): string {
  const normalized = error instanceof Error
    ? error
    : new Error('Unknown diagnostic probe failure', { cause: error })
  return `${normalized.name}: ${normalized.message}`
}

function bytesFromBase64(value: string): Uint8Array<ArrayBuffer> {
  const decoded = atob(value)
  const buffer = new ArrayBuffer(decoded.length)
  const bytes = new Uint8Array(buffer)
  for (let index = 0; index < decoded.length; index += 1) bytes[index] = decoded.charCodeAt(index)
  return bytes
}

async function nativeImageDecode(base64: string, mimeType: string): Promise<boolean> {
  if (typeof createImageBitmap !== 'function') return false
  try {
    const bitmap = await createImageBitmap(new Blob([bytesFromBase64(base64)], { type: mimeType }))
    bitmap.close()
    return true
  } catch {
    return false
  }
}

function probeOffscreenCanvasWorker(): Promise<ProbeResult> {
  if (typeof Worker === 'undefined') return Promise.resolve(result('unsupported', false))
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./diagnostics.worker.ts', import.meta.url), { type: 'module' })
    const timeout = window.setTimeout(() => {
      worker.terminate()
      reject(new Error(`Worker capability probe timed out after ${WORKER_PROBE_TIMEOUT_MS} ms`))
    }, WORKER_PROBE_TIMEOUT_MS)
    worker.onmessage = (event: MessageEvent<unknown>) => {
      window.clearTimeout(timeout)
      worker.terminate()
      if (typeof event.data !== 'boolean') {
        reject(new Error('Worker capability probe returned an invalid response'))
        return
      }
      resolve(result(event.data ? 'supported' : 'unsupported', event.data))
    }
    worker.onerror = (event) => {
      window.clearTimeout(timeout)
      worker.terminate()
      reject(new Error(event.message || 'Worker capability probe failed'))
    }
  })
}

async function probeCanvasArea(): Promise<ProbeResult> {
  const area = cachedMaxCanvasArea() ?? await probeMaxCanvasArea()
  return result(`${(area / 1_000_000).toFixed(1)} MP · ${maxSquareCanvasSide(area)}×${maxSquareCanvasSide(area)}`)
}

async function probeServiceWorker(): Promise<ProbeResult> {
  if (!('serviceWorker' in navigator)) return result('unsupported', false)
  const registration = await navigator.serviceWorker.getRegistration()
  const controlled = navigator.serviceWorker.controller !== null
  const waiting = registration?.waiting !== null && registration?.waiting !== undefined
  return result(`${controlled ? 'controlled' : 'uncontrolled'} · ${waiting ? 'update waiting' : 'no update waiting'}`, controlled)
}

async function probeShareTarget(): Promise<ProbeResult> {
  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  if (!manifestLink?.href) throw new Error('Web app manifest link is missing')
  const manifestResponse = await fetch(manifestLink.href)
  if (!manifestResponse.ok) throw new Error(`Web app manifest returned HTTP ${manifestResponse.status}`)
  const manifest = await manifestResponse.json() as ManifestShape
  if (manifest.share_target?.action !== SHARE_TARGET_PATH) return result('manifest entry missing', false)
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return result('manifest present · worker uncontrolled', false)
  }
  const handlerResponse = await fetch(SHARE_TARGET_PATH, {
    method: SHARE_TARGET_METHOD,
    body: new FormData(),
  })
  const responseText = await handlerResponse.text()
  const handlerConfirmed = handlerResponse.status === 400
    && responseText.includes(SHARE_TARGET_MISSING_FILES_MESSAGE)
  return result(handlerConfirmed ? 'registered' : `handler not confirmed · HTTP ${handlerResponse.status}`, handlerConfirmed)
}

function probeCanvasFilter(): ProbeResult {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas 2D context is unavailable')
  context.filter = 'brightness(0.5)'
  const supported = context.filter === 'brightness(0.5)'
  canvas.width = 1
  canvas.height = 1
  return result(supported ? 'supported' : 'unsupported', supported)
}

function formatStorageBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) throw new Error('Storage estimate returned invalid bytes')
  const gibibytes = bytes / (1024 ** 3)
  if (gibibytes >= 1) return `${gibibytes.toFixed(1)} GB`
  return `${(bytes / (1024 ** 2)).toFixed(1)} MB`
}

async function probeStorageEstimate(): Promise<ProbeResult> {
  if (!navigator.storage?.estimate) return result('unsupported', false)
  const estimate = await navigator.storage.estimate()
  if (estimate.usage === undefined || estimate.quota === undefined) {
    throw new Error('Storage estimate omitted usage or quota')
  }
  return result(`${formatStorageBytes(estimate.usage)} used / ${formatStorageBytes(estimate.quota)}`)
}

async function probeStoragePersisted(): Promise<ProbeResult> {
  if (!navigator.storage?.persisted) return result('unsupported', false)
  const persisted = await navigator.storage.persisted()
  return result(persisted ? 'persisted' : 'not persisted', persisted)
}

const DIAGNOSTICS: readonly DiagnosticDefinition[] = [
  {
    id: DiagnosticId.AppVersion,
    name: 'App version + commit',
    tag: ReportingTag.AppVersion,
    probe: () => result(`${__APP_VERSION__} + ${__APP_COMMIT__}`),
  },
  {
    id: DiagnosticId.BuildMode,
    name: 'Build mode',
    tag: ReportingTag.BuildMode,
    probe: () => result(import.meta.env.MODE),
  },
  {
    id: DiagnosticId.ServiceWorker,
    name: 'Service worker',
    tag: ReportingTag.ServiceWorker,
    probe: probeServiceWorker,
  },
  {
    id: DiagnosticId.OffscreenCanvasWorker,
    name: 'OffscreenCanvas in worker',
    tag: ReportingTag.OffscreenCanvasWorker,
    probe: probeOffscreenCanvasWorker,
  },
  {
    id: DiagnosticId.CanvasMaxArea,
    name: 'Canvas max area',
    tag: ReportingTag.DiagnosticsCanvasMaxArea,
    probe: probeCanvasArea,
  },
  {
    id: DiagnosticId.DeviceMemory,
    name: 'Device memory',
    tag: ReportingTag.DeviceMemory,
    probe: () => {
      const memory = (navigator as NavigatorWithDeviceMemory).deviceMemory
      return result(memory === undefined ? 'n/a' : `${memory} GB`, memory !== undefined)
    },
  },
  {
    id: DiagnosticId.HardwareConcurrency,
    name: 'Hardware concurrency',
    tag: ReportingTag.HardwareConcurrency,
    probe: () => Number.isInteger(navigator.hardwareConcurrency) && navigator.hardwareConcurrency > 0
      ? result(`${navigator.hardwareConcurrency} threads`)
      : result('n/a', false),
  },
  {
    id: DiagnosticId.ShareFiles,
    name: 'Share files supported',
    tag: ReportingTag.ShareFiles,
    probe: () => {
      const supported = canShareFiles(createShareCapabilityFiles(1, ExportFormat.Jpeg))
      return result(supported ? 'supported' : 'unsupported', supported)
    },
  },
  {
    id: DiagnosticId.ShareTarget,
    name: 'Web Share Target',
    tag: ReportingTag.ShareTarget,
    probe: probeShareTarget,
  },
  {
    id: DiagnosticId.NativeHeic,
    name: 'Native HEIC decode',
    tag: ReportingTag.NativeHeic,
    probe: async () => {
      const supported = await nativeImageDecode(HEIC_FIXTURE_BASE64, 'image/heic')
      return result(supported ? 'native' : 'fallback decoder', supported)
    },
  },
  {
    id: DiagnosticId.NativeAvif,
    name: 'Native AVIF decode',
    tag: ReportingTag.NativeAvif,
    probe: async () => {
      const supported = await nativeImageDecode(AVIF_FIXTURE_BASE64, 'image/avif')
      return result(supported ? 'native' : 'unsupported', supported)
    },
  },
  {
    id: DiagnosticId.CanvasFilter,
    name: 'Canvas filter',
    tag: ReportingTag.CanvasFilter,
    probe: probeCanvasFilter,
  },
  {
    id: DiagnosticId.StorageEstimate,
    name: 'Storage usage / quota',
    tag: ReportingTag.StorageEstimate,
    probe: probeStorageEstimate,
  },
  {
    id: DiagnosticId.StoragePersisted,
    name: 'Persistent storage',
    tag: ReportingTag.StoragePersisted,
    probe: probeStoragePersisted,
  },
  {
    id: DiagnosticId.RecentsCount,
    name: 'Recents count',
    tag: ReportingTag.RecentsCount,
    probe: async () => result(`${(await listRecentEdits()).length}`),
  },
  {
    id: DiagnosticId.DevicePixelRatio,
    name: 'Device pixel ratio',
    tag: ReportingTag.DevicePixelRatio,
    probe: () => result(`${window.devicePixelRatio}`),
  },
  {
    id: DiagnosticId.Viewport,
    name: 'Viewport',
    tag: ReportingTag.Viewport,
    probe: () => result(`${window.innerWidth}×${window.innerHeight} CSS px`),
  },
  {
    id: DiagnosticId.ColorGamutP3,
    name: 'Display P3',
    tag: ReportingTag.ColorGamutP3,
    probe: () => {
      const supported = window.matchMedia('(color-gamut: p3)').matches
      return result(supported ? 'supported' : 'not reported', supported)
    },
  },
  {
    id: DiagnosticId.ColorScheme,
    name: 'Preferred color scheme',
    tag: ReportingTag.ColorScheme,
    probe: () => result(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
  },
  {
    id: DiagnosticId.SentryEnabled,
    name: 'Sentry',
    tag: ReportingTag.SentryEnabled,
    probe: () => {
      const enabled = getReportingStatus().enabled
      return result(enabled ? 'enabled' : 'disabled', enabled)
    },
  },
  {
    id: DiagnosticId.SentryLastEventId,
    name: 'Sentry last event id',
    tag: ReportingTag.SentryLastEventId,
    probe: () => result(getReportingStatus().lastEventId ?? 'none'),
  },
]

export function validateDiagnosticsConfiguration(): void {
  const ids = new Set<DiagnosticId>()
  const tags = new Set<ReportingTag>()
  for (const diagnostic of DIAGNOSTICS) {
    if (!Object.values(DiagnosticId).includes(diagnostic.id)) {
      throw new Error(`Diagnostics configuration contains unknown id "${String(diagnostic.id)}"`)
    }
    if (ids.has(diagnostic.id)) throw new Error(`Diagnostics configuration contains duplicate id "${diagnostic.id}"`)
    if (tags.has(diagnostic.tag)) throw new Error(`Diagnostics configuration contains duplicate tag "${diagnostic.tag}"`)
    if (!diagnostic.name.trim()) throw new Error(`Diagnostic "${diagnostic.id}" has an empty name`)
    ids.add(diagnostic.id)
    tags.add(diagnostic.tag)
  }
  if (ids.size !== Object.values(DiagnosticId).length) {
    throw new Error('Diagnostics configuration does not define every diagnostic id')
  }
  for (const [name, base64, expectedBytes] of [
    ['HEIC', HEIC_FIXTURE_BASE64, HEIC_FIXTURE_BYTES],
    ['AVIF', AVIF_FIXTURE_BASE64, AVIF_FIXTURE_BYTES],
  ] as const) {
    const fixture = bytesFromBase64(base64)
    const boxType = String.fromCharCode(...fixture.subarray(4, 8))
    if (fixture.length !== expectedBytes || boxType !== 'ftyp') {
      throw new Error(`${name} diagnostic fixture must be a ${expectedBytes}-byte ISO media file`)
    }
  }
}

export async function collectDiagnostics(): Promise<DiagnosticRow[]> {
  return Promise.all(DIAGNOSTICS.map(async (diagnostic) => {
    try {
      const probeResult = await diagnostic.probe()
      return {
        id: diagnostic.id,
        name: diagnostic.name,
        tag: diagnostic.tag,
        ...probeResult,
      }
    } catch (error) {
      return {
        id: diagnostic.id,
        name: diagnostic.name,
        tag: diagnostic.tag,
        value: errorText(error),
        ok: false,
      }
    }
  }))
}

export function formatDiagnosticsText(rows: readonly DiagnosticRow[]): string {
  if (rows.length !== DIAGNOSTICS.length) {
    throw new Error(`Diagnostics copy expected ${DIAGNOSTICS.length} rows, received ${rows.length}`)
  }
  return ['Insta Little Helper diagnostics', ...rows.map((row) => `${row.name} · ${row.value} · ${row.ok ? '✓' : '✗'}`)].join('\n')
}

export async function initializeDiagnosticReportingTags(): Promise<void> {
  if (!getReportingStatus().enabled) return
  const rows = await collectDiagnostics()
  for (const row of rows) setReportingTag(row.tag, row.value)
}
